import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Alert,
  Box,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  Card,
  CardContent
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import {
  getGroupDetails,
  getGroupMembers,
  updateGroup,
  deleteGroup,
  leaveGroup,
  removeGroupMember
} from '../../services/groupService';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import InviteMemberDialog from './InviteMemberDialog';


const GroupDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [group, setGroup] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [removeMemberDialog, setRemoveMemberDialog] = useState({ open: false, member: null });
  const [groupName, setGroupName] = useState('');
  const [processing, setProcessing] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);


  useEffect(() => {
    if (id) {
      fetchGroupData();
    }
  }, [id]);

  const handleInviteSent = (result) => {
  fetchGroupData();
};

  const fetchGroupData = async () => {
    try {
      setLoading(true);
      const [groupData, membersData] = await Promise.all([
        getGroupDetails(id),
        getGroupMembers(id)
      ]);
      
      setGroup(groupData);
      setMembers(membersData);
      setGroupName(groupData.name);
    } catch (error) {
      console.error('Error fetching group data:', error);
      setError('Failed to load group details');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateGroup = async () => {
    if (!groupName.trim()) return;

    try {
      setProcessing(true);
      await updateGroup(id, { name: groupName.trim() });
      setEditDialogOpen(false);
      await fetchGroupData();
    } catch (error) {
      console.error('Error updating group:', error);
      setError('Failed to update group');
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteGroup = async () => {
    try {
      setProcessing(true);
      await deleteGroup(id);
      navigate('/groups');
    } catch (error) {
      console.error('Error deleting group:', error);
      setError('Failed to delete group');
      setProcessing(false);
    }
  };

  const handleLeaveGroup = async () => {
    try {
      setProcessing(true);
      const currentMember = members.find(m => m.role === 'owner');
      await leaveGroup(id, currentMember?.id);
      navigate('/groups');
    } catch (error) {
      console.error('Error leaving group:', error);
      setError('Failed to leave group');
      setProcessing(false);
    }
  };

  const handleRemoveMember = async (memberId) => {
    try {
      setProcessing(true);
      await removeGroupMember(id, memberId);
      setRemoveMemberDialog({ open: false, member: null });
      await fetchGroupData();
    } catch (error) {
      console.error('Error removing member:', error);
      setError('Failed to remove member');
    } finally {
      setProcessing(false);
    }
  };

  const isOwner = group?.user_role === 'owner';
  const formatDate = (dateString) => new Date(dateString).toLocaleDateString();

  if (loading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!group) {
    return (
      <Container maxWidth="md">
        <Box sx={{ py: 4 }}>
          <Alert severity="error">Group not found</Alert>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <IconButton onClick={() => navigate('/groups')} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4">
            {group.name}
          </Typography>
          <Chip
            label={group.user_role}
            color={group.user_role === 'owner' ? 'primary' : 'default'}
            sx={{ ml: 2 }}
          />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Card sx={{ mb: 3 }}>
          <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography variant="h6" gutterBottom>
                Group Information
              </Typography>
              <Typography color="textSecondary">
                <strong>Created by:</strong> {group.created_by_name}
              </Typography>
              <Typography color="textSecondary">
                <strong>Created:</strong> {formatDate(group.created_at)}
              </Typography>
              <Typography color="textSecondary">
                <strong>Members:</strong> {members.length}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton 
                onClick={() => setInviteDialogOpen(true)} 
                color="primary"
                title="Invite Member"
              >
                <PersonAddIcon />
              </IconButton>
              
              {isOwner && (
                <>
                  <IconButton onClick={() => setEditDialogOpen(true)} color="primary">
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => setDeleteDialogOpen(true)} color="error">
                    <DeleteIcon />
                  </IconButton>
                </>
              )}
            </Box>
          </Box>
        </CardContent>
        </Card>

        <Paper sx={{ mb: 3 }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6">
              Members ({members.length})
            </Typography>
          </Box>
          
          <List>
            {members.map((member, index) => (
              <React.Fragment key={member.id}>
                <ListItem>
                  <ListItemText
                    primary={member.display_name}
                    secondary={
                      <Box>
                        <Typography variant="body2" component="span">
                          {member.email}
                        </Typography>
                        <br />
                        <Typography variant="caption" color="textSecondary">
                          Joined: {formatDate(member.joined_at)}
                        </Typography>
                      </Box>
                    }
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={member.role}
                      size="small"
                      color={member.role === 'owner' ? 'primary' : 'default'}
                      variant="outlined"
                    />
                    {isOwner && member.role !== 'owner' && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setRemoveMemberDialog({ open: true, member })}
                      >
                        <PersonRemoveIcon />
                      </IconButton>
                    )}
                  </Box>
                </ListItem>
                {index < members.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Paper>

        {/* Actions */}
        {!isOwner && (
          <Box sx={{ textAlign: 'center' }}>
            <Button
              variant="outlined"
              color="error"
              startIcon={<ExitToAppIcon />}
              onClick={() => setLeaveDialogOpen(true)}
            >
              Leave Group
            </Button>
          </Box>
        )}

        {/* Edit Group Dialog */}
        <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Group</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Group Name"
              fullWidth
              variant="outlined"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialogOpen(false)} disabled={processing}>
              Cancel
            </Button>
            <Button
              onClick={handleUpdateGroup}
              variant="contained"
              disabled={processing || !groupName.trim()}
            >
              {processing ? 'Updating...' : 'Update'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Group Dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Delete Group</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete "{group.name}"? This action cannot be undone.
              All group data will be permanently removed.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)} disabled={processing}>
              Cancel
            </Button>
            <Button onClick={handleDeleteGroup} color="error" disabled={processing}>
              {processing ? 'Deleting...' : 'Delete Group'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Leave Group Dialog */}
        <Dialog open={leaveDialogOpen} onClose={() => setLeaveDialogOpen(false)}>
          <DialogTitle>Leave Group</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to leave "{group.name}"? You will no longer have access to group data.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setLeaveDialogOpen(false)} disabled={processing}>
              Cancel
            </Button>
            <Button onClick={handleLeaveGroup} color="error" disabled={processing}>
              {processing ? 'Leaving...' : 'Leave Group'}
            </Button>
          </DialogActions>
        </Dialog>

        <InviteMemberDialog
          open={inviteDialogOpen}
          onClose={() => setInviteDialogOpen(false)}
          groupId={id}
          groupName={group?.name}
          onInviteSent={handleInviteSent}
        />

        {/* Remove Member Dialog */}
        <Dialog
          open={removeMemberDialog.open}
          onClose={() => setRemoveMemberDialog({ open: false, member: null })}
        >
          <DialogTitle>Remove Member</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to remove {removeMemberDialog.member?.display_name} from the group?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setRemoveMemberDialog({ open: false, member: null })}
              disabled={processing}
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleRemoveMember(removeMemberDialog.member?.id)}
              color="error"
              disabled={processing}
            >
              {processing ? 'Removing...' : 'Remove Member'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default GroupDetailsPage;